import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useParams } from "react-router-dom";
import { API_BASE } from "../config";
import {
  RiMoneyDollarCircleLine,
  RiErrorWarningLine,
  RiCheckboxCircleFill,
  RiVerifiedBadgeLine,
  RiPriceTag3Line,
  RiArrowRightLine,
  RiRefreshLine,
  RiFileTextLine,
  RiHistoryLine,
} from "react-icons/ri";
import { Link } from "react-router-dom";

// ─── THEME COLORS ─────────────────────────────────────────────
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

/* ─── CSS ─────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:6px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}
  
  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(32px,6vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:24px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:clamp(10px,0.8vw,11px);font-weight:700;letter-spacing:.08em;text-transform:uppercase;margin-bottom:clamp(12px,2vw,20px)}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.6rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:clamp(10px,1.5vw,14px)}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(0.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:12px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0;flex-wrap:wrap}
  .t-st{background:${T.white};padding:clamp(14px,2vw,20px) clamp(18px,3vw,28px);display:flex;flex-direction:column;gap:4px;min-width:70px;flex:1}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.2vw,1.8rem);font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:clamp(9px,0.7vw,11px);color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.06em}
  
  .t-table-wrapper{background:${T.white};border-radius:16px;border:1px solid ${T.borderLight};overflow-x:auto;box-shadow:${T.shadow};-webkit-overflow-scrolling:touch}
  .t-table{width:100%;border-collapse:collapse;min-width:600px}
  .t-table th{background:${T.softTeal};padding:clamp(10px,1.5vw,16px) clamp(12px,1.5vw,20px);text-align:left;font-weight:700;color:${T.navy};border-bottom:1px solid ${T.borderLight};font-size:clamp(12px,1vw,14px)}
  .t-table td{padding:clamp(10px,1.5vw,14px) clamp(12px,1.5vw,20px);border-bottom:1px solid ${T.borderLight};color:${T.textMid};font-size:clamp(12px,1vw,14px)}
  .t-table tr:last-child td{border-bottom:none}
  .t-table tr:hover td{background:${T.offWhite}}
  .t-price{font-weight:800;font-size:clamp(16px,1.5vw,18px);color:${T.primary}}
  .t-price-closed{font-weight:800;font-size:clamp(16px,1.5vw,18px);color:#94A3B8;text-decoration:line-through}
  .t-deadline-closed{background:#FEE2E2;color:#EF4444;display:inline-block;margin-left:6px;font-size:clamp(9px,0.7vw,10px);padding:2px 6px;border-radius:4px;white-space:nowrap}
  .t-deadline-label{font-size:clamp(10px,0.8vw,12px);font-weight:400;color:${T.textMuted};display:block;margin-top:2px}
  
  .t-card{background:${T.white};border-radius:16px;border:1px solid ${T.borderLight};padding:clamp(16px,2.5vw,28px);box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-3px);box-shadow:${T.shadowLg}}
  .t-card-header{display:flex;align-items:center;gap:10px;margin-bottom:16px;flex-wrap:wrap}
  .t-card-icon{width:clamp(40px,4.5vw,48px);height:clamp(40px,4.5vw,48px);border-radius:50%;background:${T.softTeal};display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .t-card-icon svg{width:clamp(20px,2vw,24px);height:clamp(20px,2vw,24px)}
  .t-card-title{font-size:clamp(17px,1.6vw,20px);font-weight:800;color:${T.text}}
  
  .t-cta-w{padding:0 clamp(12px,4vw,64px) clamp(32px,5vw,48px);background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:16px;padding:clamp(20px,4vw,52px) clamp(16px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:24px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:3px 10px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:clamp(10px,0.8vw,11px);letter-spacing:.06em;text-transform:uppercase;margin-bottom:8px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.2rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:clamp(13px,1.2vw,14px);color:${T.textMuted};margin-top:6px;line-height:1.6}
  .t-cta-r{display:flex;flex-direction:column;align-items:flex-end;gap:10px;width:auto}
  .t-cta-btn{display:inline-flex;align-items:center;gap:6px;padding:clamp(10px,1.5vw,13px) clamp(18px,2.5vw,28px);background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:clamp(13px,1.1vw,14px);font-weight:700;transition:all .2s;justify-content:center;width:100%}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:6px;padding:clamp(10px,1.5vw,13px) clamp(18px,2.5vw,28px);background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:clamp(13px,1.1vw,14px);font-weight:700;transition:all .2s;justify-content:center;width:100%}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}
  
  .t-sw{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:clamp(40px,6vw,72px) clamp(16px,4vw,24px);gap:10px}
  .t-s-icon{width:clamp(44px,5vw,52px);height:clamp(44px,5vw,52px);border-radius:12px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:clamp(18px,2vw,22px);color:${T.primary}}
  .t-s-title{font-family:'Playfair Display',serif;font-size:clamp(18px,2vw,20px);font-weight:800;color:${T.navy}}
  .t-s-sub{font-size:clamp(13px,1.2vw,14px);color:${T.textMuted};max-width:300px;line-height:1.6}
  .t-s-btn{margin-top:4px;padding:8px 18px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:clamp(12px,1vw,13px);font-weight:500;cursor:pointer;transition:all .15s}
  .t-s-btn:hover{border-color:${T.primary};color:${T.primary}}
  
  @keyframes spin{to{transform:rotate(360deg)}}
  .t-refresh-btn{display:inline-flex;align-items:center;gap:6px;padding:clamp(6px,0.8vw,8px) clamp(12px,1.5vw,18px);border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.primary};font-family:'Inter',sans-serif;font-size:clamp(12px,1vw,13px);font-weight:600;cursor:pointer;transition:all .15s}
  .t-refresh-btn:hover{background:${T.primary};color:${T.white};border-color:${T.primary}}
  .t-last-updated{font-size:clamp(11px,0.9vw,12px);color:${T.textMuted}}
  
  .t-notes-list{display:flex;flex-direction:column;gap:clamp(8px,1vw,12px)}
  .t-note-item{padding:clamp(10px,1.5vw,14px) clamp(14px,2vw,20px);border-radius:10px;font-size:clamp(13px,1.1vw,14px);line-height:1.5}
  .t-categories-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(280px,1fr));gap:clamp(16px,2vw,24px)}
  
  .t-top-actions{display:flex;justify-content:flex-end;align-items:center;gap:clamp(8px,1.5vw,16px);margin-bottom:clamp(16px,2.5vw,24px);flex-wrap:wrap}
  
  /* Responsive overrides */
  @media(max-width:768px){
    .t-hero-i{flex-direction:column;align-items:stretch}
    .t-stats{width:100%}
    .t-table-wrapper{overflow-x:auto}
    .t-cta{flex-direction:column;align-items:stretch}
    .t-cta-r{align-items:stretch;width:100%}
    .t-cta-btn,.t-cta-btn-outline{width:100%;justify-content:center}
    .t-top-actions{justify-content:center}
    .t-categories-grid{grid-template-columns:1fr}
  }
  
  @media(max-width:480px){
    .t-hero{padding:20px 12px 24px}
    .t-hero-h{font-size:1.4rem}
    .t-hero-p{font-size:0.85rem}
    .t-stats{flex-direction:column}
    .t-st{padding:10px 14px;min-width:auto}
    .t-st-n{font-size:1.2rem}
    .t-card{padding:14px}
    .t-card-header{gap:8px}
    .t-card-icon{width:34px;height:34px}
    .t-card-title{font-size:16px}
    .t-table th,.t-table td{padding:8px 10px;font-size:12px}
    .t-price,.t-price-closed{font-size:14px}
    .t-deadline-closed{font-size:8px;padding:1px 4px}
    .t-cta{padding:16px 14px}
    .t-cta-h{font-size:1.1rem}
    .t-note-item{font-size:13px;padding:8px 12px}
  }
  
  @media(max-width:360px){
    .t-hero-h{font-size:1.2rem}
    .t-table th,.t-table td{font-size:11px;padding:6px 8px}
    .t-price,.t-price-closed{font-size:13px}
    .t-card-title{font-size:14px}
    .t-refresh-btn{font-size:11px;padding:4px 10px}
  }
`;

const RegistrationFeePage = () => {
  const { id: eventId } = useParams();
  const [feeData, setFeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ── Fetch using the same endpoint as admin panel ──
  const fetchRegistrationFee = async (forceRefresh = false) => {
    if (!eventId) {
      setError("No event specified");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the same endpoint as the admin panel: /event/{eventId}
      const cacheBuster = forceRefresh ? `?_t=${Date.now()}` : '';
      const url = `${API_BASE}/api/registration-fee/event/${eventId}${cacheBuster}`;
      console.log("📡 Fetching registration fee from:", url);

      const { data } = await axios.get(url, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        }
      });

      console.log("📦 Response:", data);

      if (data.success && data.data) {
        let activeData = null;
        // The endpoint returns an array of all versions
        if (Array.isArray(data.data)) {
          // Find the active version
          const active = data.data.find(f => f.isActive === true);
          if (active) {
            activeData = active;
          } else if (data.data.length > 0) {
            // If none is active, pick the first one
            activeData = data.data[0];
          }
        } else {
          // If the API returns a single object (fallback)
          activeData = data.data;
        }

        if (activeData) {
          console.log("✅ Setting feeData:", activeData);
          setFeeData(activeData);
          setLastUpdated(new Date().toLocaleTimeString());
          setError(null);
        } else {
          setError("No registration fee data available for this event");
          setFeeData(null);
        }
      } else {
        setError("No registration fee data available for this event");
        setFeeData(null);
      }
    } catch (err) {
      console.error("❌ Error fetching registration fee:", err);
      if (err.response?.status === 404) {
        setError("Registration fee information not available for this event yet.");
      } else {
        setError("Failed to load registration fee data");
      }
      setFeeData(null);
    } finally {
      setLoading(false);
    }
  };

  // ── Initial fetch ──
  useEffect(() => {
    if (eventId) {
      fetchRegistrationFee();
    } else {
      setError("No event specified");
      setLoading(false);
    }
  }, [eventId]);

  const isDeadlinePast = (date) => {
    if (!date) return true;
    return new Date(date) < new Date();
  };

  const formatDate = (date) => {
    if (!date) return "Closed";
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ── Debug log before render ──
  console.log("🔄 Render state:", { loading, error, feeData: feeData ? "present" : "null" });

  // ── Loading State ──
  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiMoneyDollarCircleLine size={12} />
                Meetings21 · Registration
              </div>
              <h1 className="t-hero-h">Registration <em>Fee</em></h1>
              <p className="t-hero-p">Loading registration information...</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
                <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
                <div style={{ width: 6, height: 6, background: T.accentGold, transform: 'rotate(45deg)' }} />
                <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiRefreshLine size={20} style={{ animation: 'spin 1s linear infinite' }} /></div>
          <p className="t-s-title">Loading Registration Fee</p>
          <p className="t-s-sub">Please wait while we load the registration information...</p>
        </div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── Error / No Data State ──
  if (error || !feeData) {
    console.warn("⚠️ Rendering error/no-data state:", { error, feeData });
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiMoneyDollarCircleLine size={12} />
                Meetings21 · Registration
              </div>
              <h1 className="t-hero-h">Registration <em>Fee</em></h1>
              <p className="t-hero-p">Essential information for conference registration</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
                <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
                <div style={{ width: 6, height: 6, background: T.accentGold, transform: 'rotate(45deg)' }} />
                <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiFileTextLine size={24} /></div>
          <p className="t-s-title">No Registration Data Available</p>
          <p className="t-s-sub">{error || "Registration fee information will be available soon."}</p>
          <button className="t-s-btn" onClick={() => fetchRegistrationFee(true)}>
            <RiRefreshLine size={14} style={{ display: 'inline', marginRight: 6 }} />
            Refresh
          </button>
        </div>
      </div>
    );
  }

  // ── Data Display ──
  console.log("✅ Rendering data for:", feeData.title);
  const activeCategories = feeData.categories?.filter(c => c.isActive).sort((a, b) => a.order - b.order) || [];
  const activeNotes = feeData.importantNotes?.filter(n => n.isActive).sort((a, b) => a.order - b.order) || [];

  const superEarlyPast = isDeadlinePast(feeData.superEarlyDeadline);
  const earlyPast = isDeadlinePast(feeData.earlyDeadline);
  const standardPast = isDeadlinePast(feeData.regularDeadline);

  const totalCategories = activeCategories.length;

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ══════════ HERO ══════════ */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-hero-lbl">
              <RiMoneyDollarCircleLine size={12} />
              Meetings21 · Registration
            </div>
            <h1 className="t-hero-h">
              Registration <em>Fee</em>
            </h1>
            <p className="t-hero-p">
              Choose your preferred registration category and secure your participation
              at the premier academic conference of the year.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 6, height: 6, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 60, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{totalCategories}</span>
              <span className="t-st-l">Categories</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{feeData.currency || "USD"}</span>
              <span className="t-st-l">Currency</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{feeData.version || "2026"}</span>
              <span className="t-st-l">Edition</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(12px,4vw,64px) ' + 'clamp(40px,6vw,80px)' }}>

        {/* ── Refresh & Last Updated ── */}
        <div className="t-top-actions">
          <span className="t-last-updated">
            <RiHistoryLine size={13} style={{ display: 'inline', marginRight: 4 }} />
            Last updated: {lastUpdated || 'Just now'}
          </span>
          <button
            className="t-refresh-btn"
            onClick={() => fetchRegistrationFee(true)}
          >
            <RiRefreshLine size={14} /> Refresh Data
          </button>
        </div>

        {/* ─── SECTION 1: IMPORTANT NOTES ─── */}
        {activeNotes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="t-card"
            style={{ marginBottom: 'clamp(24px,3vw,32px)' }}
          >
            <div className="t-card-header">
              <div className="t-card-icon">
                <RiErrorWarningLine size={20} style={{ color: T.primary }} />
              </div>
              <h2 className="t-card-title">Important Information</h2>
            </div>
            <div className="t-notes-list">
              {activeNotes.map((note, idx) => {
                const severityColors = {
                  error: { bg: '#FEE2E2', color: '#EF4444', border: '#FECACA' },
                  warning: { bg: '#FEF3C7', color: '#D97706', border: '#FDE68A' },
                  info: { bg: T.softTeal, color: T.primary, border: T.border },
                  success: { bg: '#DCFCE7', color: '#16A34A', border: '#BBF7D0' }
                };
                const colors = severityColors[note.severity] || severityColors.info;
                return (
                  <div
                    key={idx}
                    className="t-note-item"
                    style={{
                      background: colors.bg,
                      borderLeft: `4px solid ${colors.color}`,
                      borderRadius: 10,
                    }}
                  >
                    <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: colors.color, lineHeight: 1.5 }}>{note.message}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ─── SECTION 2: REGISTRATION CATEGORIES & PRICING TABLE ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ marginBottom: 'clamp(32px,4vw,48px)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'clamp(16px,2.5vw,24px)', flexWrap: 'wrap' }}>
            <div style={{ width: 'clamp(40px,4.5vw,48px)', height: 'clamp(40px,4.5vw,48px)', borderRadius: '50%', background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <RiPriceTag3Line size={20} style={{ color: T.primary }} />
            </div>
            <div>
              <h2 style={{ fontSize: 'clamp(20px,2.2vw,24px)', fontWeight: 800, color: T.text }}>Registration Categories & Pricing</h2>
              <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.textMuted }}>All amounts are in {feeData.currency || "USD"}</p>
            </div>
          </div>

          {activeCategories.length > 0 ? (
            <div className="t-table-wrapper">
              <table className="t-table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 'clamp(120px,15vw,180px)' }}>Registration Category</th>
                    <th style={{ textAlign: 'center', minWidth: 'clamp(120px,15vw,180px)' }}>
                      Super Earlybird
                      <span className="t-deadline-label">{formatDate(feeData.superEarlyDeadline)}</span>
                      {superEarlyPast && <span className="t-deadline-closed">CLOSED</span>}
                    </th>
                    <th style={{ textAlign: 'center', minWidth: 'clamp(120px,15vw,180px)' }}>
                      Earlybird
                      <span className="t-deadline-label">{formatDate(feeData.earlyDeadline)}</span>
                      {earlyPast && <span className="t-deadline-closed">CLOSED</span>}
                    </th>
                    <th style={{ textAlign: 'center', minWidth: 'clamp(120px,15vw,180px)' }}>
                      Standard
                      <span className="t-deadline-label">{formatDate(feeData.regularDeadline)}</span>
                      {standardPast && <span className="t-deadline-closed">CLOSED</span>}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activeCategories.map((cat, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: 600, color: T.text }}>
                        {cat.name}
                        {cat.benefits && cat.benefits.length > 0 && (
                          <div style={{ fontSize: 'clamp(10px,0.8vw,11px)', color: T.textMuted, marginTop: 4 }}>
                            {cat.benefits.slice(0, 2).map((b, i) => (
                              <span key={i}>✓ {b} </span>
                            ))}
                            {cat.benefits.length > 2 && <span>+{cat.benefits.length - 2} more</span>}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {superEarlyPast ? (
                          <span className="t-price-closed">${cat.superEarlyPrice || '-'}</span>
                        ) : (
                          <span className="t-price">${cat.superEarlyPrice || '-'}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {earlyPast ? (
                          <span className="t-price-closed">${cat.earlyPrice || '-'}</span>
                        ) : (
                          <span className="t-price">${cat.earlyPrice || '-'}</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {standardPast ? (
                          <span className="t-price-closed">${cat.regularPrice || '-'}</span>
                        ) : (
                          <span className="t-price">${cat.regularPrice || '-'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 'clamp(24px,4vw,40px)', background: T.white, borderRadius: 14, border: `1px solid ${T.borderLight}` }}>
              <p style={{ color: T.textMuted, fontSize: 'clamp(13px,1.1vw,14px)' }}>No registration categories have been added yet.</p>
              <p style={{ color: T.textMuted, fontSize: 'clamp(12px,1vw,13px)', marginTop: 4 }}>Check back later for updated pricing.</p>
            </div>
          )}

          <p style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted, marginTop: 14, textAlign: 'center' }}>
            * All fees are inclusive of applicable taxes. Accommodation not included unless specified.
          </p>
        </motion.div>

        {/* ─── SECTION 3: REGISTRATION CATEGORIES EXPLAINED ─── */}
        {activeCategories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: 'clamp(32px,4vw,48px)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 'clamp(16px,2.5vw,24px)', flexWrap: 'wrap' }}>
              <div style={{ width: 'clamp(40px,4.5vw,48px)', height: 'clamp(40px,4.5vw,48px)', borderRadius: '50%', background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <RiVerifiedBadgeLine size={20} style={{ color: T.primary }} />
              </div>
              <div>
                <h2 style={{ fontSize: 'clamp(20px,2.2vw,24px)', fontWeight: 800, color: T.text }}>Registration Categories Explained</h2>
                <p style={{ fontSize: 'clamp(13px,1.1vw,14px)', color: T.textMuted }}>What's included in each registration category</p>
              </div>
            </div>

            <div className="t-categories-grid">
              {activeCategories.map((cat, idx) => (
                <div key={idx} className="t-card" style={{ padding: 'clamp(16px,2.5vw,24px)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div style={{ width: 'clamp(40px,4vw,48px)', height: 'clamp(40px,4vw,48px)', borderRadius: 14, background: T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <RiPriceTag3Line size={18} style={{ color: T.primary }} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 'clamp(16px,1.4vw,18px)', fontWeight: 800, color: T.text }}>{cat.name}</h3>
                      <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>Super Early: ${cat.superEarlyPrice}</span>
                        <span style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>Early: ${cat.earlyPrice}</span>
                        <span style={{ fontSize: 'clamp(11px,0.9vw,12px)', color: T.textMuted }}>Regular: ${cat.regularPrice}</span>
                      </div>
                    </div>
                  </div>
                  {cat.benefits && cat.benefits.length > 0 && (
                    <div style={{ borderTop: `1px solid ${T.borderLight}`, paddingTop: 12 }}>
                      {cat.benefits.map((benefit, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <RiCheckboxCircleFill size={13} style={{ color: '#16A34A', flexShrink: 0 }} />
                          <span style={{ fontSize: 'clamp(12px,1vw,13px)', color: T.textMid }}>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* ─── SECTION 4: CTA - REGISTER NOW ─── */}
        <div className="t-cta-w" style={{ padding: 0 }}>
          <motion.div
            className="t-cta"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="t-cta-tag">Secure Your Seat</div>
              <h2 className="t-cta-h">Ready to Register?<br />Join the Premier Academic Event</h2>
              <p className="t-cta-p">Complete your registration online and reserve your participation. Join leading researchers and scholars from around the world.</p>
            </div>
            <div className="t-cta-r">
              <Link
                to={eventId ? `/register?eventId=${eventId}` : "/register"}
                className="t-cta-btn"
              >
                Register Now <RiArrowRightLine size={14} />
              </Link>
              <Link to={`/contact${eventId ? `?eventId=${eventId}` : ''}`} className="t-cta-btn-outline">
                Contact Secretary
                <RiArrowRightLine size={14} />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationFeePage;